<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import { apiMethods } from "../../Services/index.svelte";

    import { isMobile } from "../../stores";

    import {
        SearchInput,
        SvelteTable,
        SearchSelect,
        PageLoading,
    } from "../../Components/Common/index.svelte";

    import {
        replaceURL,
        getText,
        getFormatDate,
        showError,
        decodeSearchParams,
        encodeSearchParams,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    const year = new Date().getFullYear();

    export let params = {};

    let returnPublicHolidaysArr = [],
        isLoadingPublicHolidays = false,
        showFilters = false,
        filters = { year: year },
        page = 0,
        pageSize = 25,
        count,
        years = [];

    onMount(async () => {
        if (params.query) updateSearchParamsByQuery(params.query);

        await loadPublicHolidays(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();

                if (arr[i].includes("year="))
                    filters.year = arr[i].split("=")[1].toString();

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1].toString();
            }

            updateURL();
        }
    };

    const columns = [
        {
            key: "date",
            title: "Date",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => getFormatDate(v.date),
        },
        {
            key: "state",
            title: "State",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => getText(v.state),
        },
        {
            key: "name",
            title: "Name",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => getText(v.name),
        },
    ];

    const sortableHeaderKeys = ["name", "date"];

    const loadPublicHolidays = async (start, limit, params) => {
        isLoadingPublicHolidays = true;

        if (filters.year === null) params["filter[year]"] = year;

        await apiMethods.getMethod
            .getPublicHolidays(start, limit, params)
            .then((res) => _handlePublicHolidays(res));

        isLoadingPublicHolidays = false;
    };

    const _handlePublicHolidays = (res) => {
        if (res.error) showError(res.error);
        else {
            count = res.total;
            returnPublicHolidaysArr = [...res.results];
        }
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const handleFilterSelect = () => {
        page = 0;
        updateURL();
        loadPublicHolidays(0, pageSize, _getSearchParams());
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");

    const handleClickCol = ({ target: { className } }, rowData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}desc`;
            else filters.sort = key;
            updateURL();
            loadPublicHolidays(0, pageSize, _getSearchParams());
        }
    };

    for (let index = year; index <= year + 4; index++) {
        years.push(index);
    }

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        if (filters.search) params.search = filters.search;

        if (filters.year) params["filter[year]"] = filters.year;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[year]"])
                str += `&year=${params["filter[year]"]}`;
        }

        return encodeSearchParams(str);
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadPublicHolidays(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadPublicHolidays(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/PublicHolidays/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };
</script>

<div class="flex flex-col flex-1 gap-y-4 pb-6">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}
    {#if showFilters || !$isMobile}
        <div
            class="flex flex-0 flex-col gap-4 {$isMobile
                ? 'items-start'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            <div class="flex flex-row items-center justify-between flex-wrap">
                <div class="flex gap-4 flex-wrap">
                    <SearchInput
                        bind:value={filters.search}
                        title="Search"
                        inputFunc={handleFilterSelect}
                        isLong
                    />
                    <SearchSelect
                        title="Year"
                        bind:value={filters.year}
                        onChangeFunc={handleFilterSelect}
                        isLong
                    >
                        {#each years as year}
                            <option value={year}>{year}</option>
                        {/each}
                    </SearchSelect>
                </div>
            </div>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoadingPublicHolidays}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnPublicHolidaysArr}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>
</div>

<TableFooter
    {page}
    {pageSize}
    {count}
    tableData={returnPublicHolidaysArr}
    {onPageChange}
    {onPageSizeChange}
    csvConfig={{
        columns,
        data: returnPublicHolidaysArr,
        fileName: "Public Holidays",
        allDataApi: async () =>
            await apiMethods.getMethod.getPublicHolidays(
                0,
                999999,
                _getSearchParams(),
            ),
    }}
/>
